/**
 * UIController Module
 * Handles UI state management, tab switching, format selection, and form controls
 */
import type { BackgroundTab, ExportFormat } from '../../types';
import type { Analytics } from '../../services/analytics';

export class UIController {
    analytics: Analytics;
    currentFormat: ExportFormat;
    activeTab: BackgroundTab;

    constructor(analytics: Analytics) {
        this.analytics = analytics;
        this.currentFormat = 'png';
        this.activeTab = 'color';
    }

    /**
     * Set current format (called by FormatSelector)
     */
    setCurrentFormat(format: ExportFormat): void {
        this.currentFormat = format;
    }

    /**
     * Initialize UI event bindings
     */
    bindUIEvents(): void {
        // Tab switching
        document.querySelectorAll('.tab-button').forEach(button => {
            button.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                const tab = target.dataset.tab as BackgroundTab;
                if (tab) {
                    this.switchTab(tab);
                }
            });
        });
        
        // Note: Format selection now handled by FormatSelector component
    }

    /**
     * Switch between tabs (color/image)
     * @param tabName - Tab to switch to
     */
    switchTab(tabName: BackgroundTab): void {
        this.activeTab = tabName;
        
        // Update tab buttons
        document.querySelectorAll('.tab-button').forEach(btn => {
            const element = btn as HTMLElement;
            btn.classList.toggle('active', element.dataset.tab === tabName);
        });
        
        // Update tab content visibility
        document.querySelectorAll('.tab-content').forEach(content => {
            if (content.id === `${tabName}-tab`) {
                content.classList.add('active');
            } else {
                content.classList.remove('active');
            }
        });
        
        this.analytics.trackTabSwitch(tabName);
    }

    /**
     * Select image format (deprecated - use FormatSelector component)
     * @param format - Format to select (png, jpg, webp)
     * @deprecated Use FormatSelector component instead
     */
    selectFormat(format: ExportFormat): void {
        this.currentFormat = format;
        // Format selection now handled by FormatSelector component
    }

    /**
     * Update color preview elements
     * @param type - 'background' or 'text'
     * @param color - Color value
     */
    updateColorPreview(type: 'background' | 'text', color: string): void {
        if (window.ColorUtils && !window.ColorUtils.isValidColor(color)) return;
        
        const preview = type === 'background' 
            ? document.querySelector('.bg-preview') as HTMLElement
            : document.querySelector('.text-preview') as HTMLElement;
            
        if (preview) {
            preview.style.backgroundColor = color;
        }
    }

    /**
     * Initialize color previews
     */
    initializeColorPreviews(): void {
        // Initialize background color preview
        const bgColorInput = document.getElementById('backgroundColorText') as HTMLInputElement;
        if (bgColorInput) {
            const bgColor = bgColorInput.value;
            this.updateColorPreview('background', bgColor);
        }
        
        // Initialize text color preview
        const textColorInput = document.getElementById('textColorText') as HTMLInputElement;
        if (textColorInput) {
            const textColor = textColorInput.value;
            this.updateColorPreview('text', textColor);
        }
    }

    /**
     * Update dimension and format info displays
     * @param width - Canvas width
     * @param height - Canvas height
     */
    updateInfoDisplays(width: number, height: number): void {
        const dimensionInfo = document.getElementById('dimensionInfo');
        if (dimensionInfo) {
            dimensionInfo.textContent = `${width} × ${height} px`;
        }
        
        const formatInfo = document.getElementById('formatInfo');
        if (formatInfo) {
            formatInfo.textContent = `${this.currentFormat.toUpperCase()} Format`;
        }
    }

    /**
     * Reset all form values to defaults
     */
    resetFormValues(): void {
        const widthInput = document.getElementById('widthInput') as HTMLInputElement;
        const heightInput = document.getElementById('heightInput') as HTMLInputElement;
        const bgColorInput = document.getElementById('backgroundColorText') as HTMLInputElement;
        const textContentInput = document.getElementById('textContent') as HTMLInputElement;
        const textColorInput = document.getElementById('textColorText') as HTMLInputElement;
        
        if (widthInput) widthInput.value = '800';
        if (heightInput) heightInput.value = '600';
        if (bgColorInput) bgColorInput.value = '#3B82F6';
        if (textContentInput) textContentInput.value = '';
        if (textColorInput) textColorInput.value = '#111827';
        
        // Reset color previews
        const bgPreview = document.querySelector('.bg-preview') as HTMLElement;
        if (bgPreview) bgPreview.style.backgroundColor = '#3B82F6';
        
        const textPreview = document.querySelector('.text-preview') as HTMLElement;
        if (textPreview) textPreview.style.backgroundColor = '#111827';
        
        // Clear the sample text
        const sampleText = document.querySelector('.sample-text') as HTMLElement;
        if (sampleText) sampleText.textContent = '';
        
        // Reset format to PNG
        this.selectFormat('png');
        
        // Reset to color tab
        this.switchTab('color');
    }

    /**
     * Open color palette (cycles through default colors)
     * @param button - Palette button that was clicked
     * @param updateCallback - Callback to update preview
     */
    openColorPalette(button: HTMLElement, updateCallback?: () => void): void {
        // Get default color palette
        const colors = window.ColorUtils ? window.ColorUtils.getDefaultPalette() : [];
        
        // For now, just cycle through colors on click
        const isBackground = button.closest('.background-controls') !== null;
        const currentInput = isBackground 
            ? document.getElementById('backgroundColorText') as HTMLInputElement
            : document.getElementById('textColorText') as HTMLInputElement;
        
        if (!currentInput) return;
        
        const currentIndex = colors.indexOf(currentInput.value);
        const nextIndex = (currentIndex + 1) % colors.length;
        const nextColor = colors[nextIndex];
        
        currentInput.value = nextColor;
        this.updateColorPreview(isBackground ? 'background' : 'text', nextColor);
        
        if (updateCallback) {
            updateCallback();
        }
    }

    /**
     * Get current format
     * @returns Current format
     */
    getCurrentFormat(): ExportFormat {
        return this.currentFormat;
    }

    /**
     * Get active tab
     * @returns Active tab name
     */
    getActiveTab(): BackgroundTab {
        return this.activeTab;
    }

    /**
     * Show loading state on button
     * @param button - Button element
     * @param text - Loading text
     */
    showButtonLoading(button: HTMLButtonElement, text: string = 'Loading...'): void {
        button.innerHTML = `
            <div class="button-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="8" stroke="white" stroke-width="2" stroke-linecap="round" stroke-dasharray="32" stroke-dashoffset="32">
                        <animateTransform attributeName="transform" type="rotate" dur="1s" repeatCount="indefinite" values="0 10 10;360 10 10"/>
                    </circle>
                </svg>
            </div>
            <span>${text}</span>
        `;
        button.disabled = true;
    }

    /**
     * Reset button to normal state
     * @param button - Button element
     * @param originalText - Original button HTML
     */
    resetButton(button: HTMLButtonElement, originalText: string): void {
        button.innerHTML = originalText;
        button.disabled = false;
    }
}
